import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import client, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { AuthShell, Field } from "@/pages/Login";

export default function ResetPassword() {
  const nav = useNavigate();
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <AuthShell title={t("reset_bad_title")} subtitle={t("reset_bad_sub")}>
        <div className="glass rounded-2xl p-6 text-center" data-testid="reset-invalid">
          <XCircle className="text-red-400 mx-auto" size={40} />
          <Link to="/forgot" className="inline-block mt-4 text-cyan-300 font-semibold" data-testid="reset-request-new">{t("send_reset")}</Link>
        </div>
      </AuthShell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(t("pw_min6")); return; }
    if (password !== confirm) { toast.error(t("pw_no_match")); return; }
    setLoading(true);
    try {
      await client.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success(t("reset_ok_title"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={done ? t("reset_ok_title") : t("reset_new_title")} subtitle={done ? t("reset_ok_sub") : t("reset_new_sub")}>
      {done ? (
        <div className="glass rounded-2xl p-6 text-center" data-testid="reset-success">
          <CheckCircle2 className="text-emerald-400 mx-auto" size={40} />
          <button onClick={() => nav("/login")} data-testid="reset-to-login" className="mt-5 w-full mel-gradient text-white font-semibold py-3.5 rounded-2xl press">{t("log_in")}</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field icon={Lock} type="password" required placeholder={t("new_password_ph")} value={password}
            onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" />
          <Field icon={Lock} type="password" required placeholder={t("confirm_password_ph")} value={confirm}
            onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" />
          <button disabled={loading} data-testid="reset-submit"
            className="w-full mel-gradient text-white font-semibold py-4 rounded-2xl shadow-[0_14px_34px_-14px_rgba(37,99,235,0.8)] flex items-center justify-center gap-2 disabled:opacity-60 press">
            {loading ? t("updating_word") : <>{t("set_new_password")} <ArrowRight size={18} /></>}
          </button>
          <p className="text-center text-slate-400 mt-4">
            <Link to="/login" className="text-cyan-300 font-semibold">{t("back_login")}</Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}
